import React, { Component } from 'react';
import { shape } from 'prop-types';
import { withRouter } from 'react-router';
import I18n from 'i18n-js';
import { ddRum, rumErrorCtx, SEVERITY } from '@web-platform/browser-rum';
import { Spin, Icon } from 'grab-ui';
import { paxLoginSuccessAction } from 'actions/login-actions';
import { ROUTES_V2_PATHS } from 'constants/route-constants';
import { initGrabIDAuthClient } from 'helper/grab-id-auth-helper';
import {
  STORAGE_LOGIN_REDIRECT,
  STORAGE_GFB_TOKEN,
  STORAGE_USER_COUNTRY_CODE,
  STORAGE_USER_COUNTRY_CODE_PERSONAL,
} from 'constants/app-constants';
import { getUserFeatureFlagsAsync } from 'helper/get-user-feature-flags-helper';
import { setUsePaxPersonalPayments } from 'helper/user';
import { GLOBAL_INIT_CLIENT } from 'actions/app-actions';
import { connect } from 'react-redux';
import Storage from 'grab-storage';
import { userInfoSelector } from 'selectors/app-selector';
import {
  SCRIBE_MEX_APP_STATES,
  SCRIBE_MEX_APP_EVENTS,
  getCommonFacet,
  scribeTrackEvent,
} from 'applications/scribe/instrumentations/track';
import { getDefaultErrorContext } from 'common/utils/rum-utils';
import { isFromMexApp } from 'helper/mex-app-helper';
import _isEmpty from 'lodash/isEmpty';
import css from './grab-id-redirect.less';
import { setAuthHintLocal } from '../../../helper/session-helper';

const DEFAULT_COUNTRY_CODE = 'ID';

class GrabIDRedirect extends Component {
  static propTypes = {
    history: shape({}).isRequired,
    userInfo: shape({}).isRequired,
  };

  constructor(props) {
    super(props);
    this.redirectTimeout = null;
    this.grabIdAuthClient = initGrabIDAuthClient();
    this.state = {
      showRedirectLink: false,
    };
  }

  componentDidMount() {
    // Set default country code for personal profile
    const countryCode = Storage.getItem(STORAGE_USER_COUNTRY_CODE);
    if (_isEmpty(countryCode)) {
      Storage.setItem(STORAGE_USER_COUNTRY_CODE_PERSONAL, DEFAULT_COUNTRY_CODE);
      Storage.setItem(STORAGE_USER_COUNTRY_CODE, DEFAULT_COUNTRY_CODE);
    } else {
      Storage.setItem(STORAGE_USER_COUNTRY_CODE_PERSONAL, countryCode);
    }

    if (window.location.search && window.location.search.includes('?code=')) {
      setAuthHintLocal();
      this.handleAuthResponse();
    } else if (
      window.location.search.includes('error_description=REGISTER_NOT_ALLOWED')
    ) {
      this.redirectToDownloadApp();
    } else {
      this.redirectToLogin();
    }
  }

  onClickRedirectGrabId = async () => {
    const { history, startInit } = this.props;
    const loginRedirect = window.localStorage.getItem(STORAGE_LOGIN_REDIRECT);

    setUsePaxPersonalPayments(true);
    paxLoginSuccessAction();
    clearTimeout(this.redirectTimeout);
    await getUserFeatureFlagsAsync();
    startInit();

    if (isFromMexApp()) {
      const { userInfo } = this.props;
      await scribeTrackEvent({
        stateName: SCRIBE_MEX_APP_STATES.EXPRESS_MEX_APP_LOGIN,
        eventName: SCRIBE_MEX_APP_EVENTS.GRAB_ACCOUNT_LOGIN,
        facets: {
          ...getCommonFacet(userInfo),
        },
        forceUpdate: true,
      });
    }

    if (loginRedirect) {
      window.localStorage.removeItem(STORAGE_LOGIN_REDIRECT);
      history.replace(loginRedirect);
    } else {
      history.replace(ROUTES_V2_PATHS.home);
    }
  };

  redirectToLogin = () => {
    const { history } = this.props;
    history.replace(ROUTES_V2_PATHS.login);
  };

  redirectToDownloadApp = () => {
    window.location.replace(ROUTES_V2_PATHS.errorGrabIDUser);
  };

  handleAuthResponse = async () => {
    try {
      await this.grabIdAuthClient.parseAuthResponse();
      const accessToken = await this.grabIdAuthClient.getAuthToken();
      if (accessToken) {
        // implies successful login
        window.localStorage.removeItem(STORAGE_GFB_TOKEN); // clear GFB auth after login GrabID
        this.setState({
          showRedirectLink: true,
        });
        this.redirectTimeout = setTimeout(() => {
          this.onClickRedirectGrabId();
        }, 3000);
      } else {
        this.redirectToLogin();
      }
    } catch (error) {
      const errorCtx = rumErrorCtx(getDefaultErrorContext())
        .component('GrabIDRedirect')
        .message(error.message || 'Failed to handle auth callback')
        .severity(SEVERITY.HIGH)
        .build();
      ddRum.addError(error, errorCtx);
      // eslint-disable-next-line no-console
      console.error(error);
      this.redirectToLogin();
    }
  };

  render() {
    const { showRedirectLink } = this.state;
    return (
      <div className={css.wrapper}>
        <div className={css.contentWrapper}>
          <img src={require('public/images/redirect.svg')} alt="" />
          <div className={css.loadingWrapper}>
            <Spin
              indicator={<Icon type="loading" style={{ fontSize: 24 }} spin />}
            />
            <div className={css.textWrapper}>
              <h4>{I18n.t('v2.redirectGrabIdPage.message')}</h4>
              {showRedirectLink && (
                <h6>
                  {I18n.t('v2.redirectGrabIdPage.clickPrefix')}{' '}
                  <span
                    onClick={this.onClickRedirectGrabId}
                    role="button"
                    tabIndex="0"
                    onKeyDown={null}
                  >
                    {I18n.t('v2.redirectGrabIdPage.click')}
                  </span>
                </h6>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  userInfo: userInfoSelector(state),
});

export const mapDispatchToProps = dispatch => ({
  startInit: () => dispatch({ type: GLOBAL_INIT_CLIENT }),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(GrabIDRedirect),
);
